import React, {Fragment, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {Button, FloatingLabel, Form} from "react-bootstrap";
import {useLocation} from "react-router-dom";
import {getCourseHistoryActions, getCourseHistoryAll, getCourseList} from "../api/CourseApi";
import {getUser} from "../api/UserApi";
import {getCourseHistoryActionDisplay} from "../course/courseUtils";
import {toolTipRenderer} from "../utils/gridUtils";
import {dateTimeToStr} from "../utils/usacmUtils";
import {getErrorMessageForAllErrors} from "../utils/formUtils";

/**
 * The CourseHistory log on the Tracking screen.
 * The filters above the grid are applied on the server, the grid's own column filters and sorting work on the
 * page of rows that was loaded.
 */
export function TrackingCourseHistory() {
  const [historyList, setHistoryList] = useState([]);
  const gridRef = useRef();
  const {search} = useLocation();
  const searchParams = new URLSearchParams(search);
  const filterUserId = searchParams.get('userid') || '';
  const [filterUserData, setFilterUserData] = useState(null);
  const hasFewRows = (historyList || []).length < 12;
  const [showAllConf, setShowAllConf] = useState(false);
  const [dateFilterStart, setDateFilterStart] = useState('');
  const [dateFilterEnd, setDateFilterEnd] = useState('');
  const [courseId, setCourseId] = useState('');
  const [courseList, setCourseList] = useState([]);
  const [action, setAction] = useState('');
  const [actionList, setActionList] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [pageNum, setPageNum] = useState('1');
  const [pageSize, setPageSize] = useState('1000');
  const [quickFilter, setQuickFilter] = useState('');
  const [errors, setErrors] = useState([]);

  function loadHistory() {
    setErrors([]);
    getCourseHistoryAll(showAllConf, filterUserId, dateFilterStart, dateFilterEnd, courseId, action, userSearch,
      pageNum, pageSize, (code, data, errors) => {
        if (code === 200) {
          setHistoryList(data);
        } else {
          setErrors(errors);
        }
      });
  }

  useEffect(() => {
    if (filterUserId) {
      getUser(filterUserId, (code, data, errors) => {
        if (code === 200) {
          setFilterUserData(data);
        }
      });
    }
    getCourseHistoryActions((code, data, errors) => {
      if (code === 200) {
        setActionList(data);
      }
    });
    getCourseList((code, data, errors) => {
      if (code === 200) {
        setCourseList(data);
      }
    });
    loadHistory();
  }, [filterUserId]);

  function userDisplay(name, email) {
    return email ? `${name} (${email})` : '';
  }

  const colDefs = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90, minWidth: 90,
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'Date (browser timezone)',
      width: 200, minWidth: 200,
      sort: 'desc',
      // This will parse the GMT date string into a JS Date in the browser's timezone
      valueGetter: params => new Date(Date.parse(params.data?.create_date)),
      valueFormatter: params => dateTimeToStr(params.value),
      getQuickFilterText: params => dateTimeToStr(params.value),
      filter: 'agDateColumnFilter',
      filterParams: {
        // The filter picks a day (at midnight), so compare the row's date without its time
        comparator: (filterDate, cellValue) => {
          const cellDay = new Date(cellValue.getFullYear(), cellValue.getMonth(), cellValue.getDate());
          return cellDay < filterDate ? -1 : (cellDay > filterDate ? 1 : 0);
        },
      },
    },
    {
      headerName: 'User',
      minWidth: 200, flex: 3,
      valueGetter: params => userDisplay(params.data.user_name, params.data.user_email),
      cellRenderer: toolTipRenderer,
    },
    {
      headerName: 'Done By',
      minWidth: 200, flex: 3,
      valueGetter: params => params.data.create_user_id ?
        userDisplay(params.data.create_user_name, params.data.create_user_email) : 'System',
      cellRenderer: toolTipRenderer,
    },
    {
      headerName: 'Course',
      minWidth: 180, flex: 3,
      valueGetter: params => {
        if (!params.data.course_id) {
          return '';
        }
        return `${params.data.course_title} (#${params.data.course_id})`;
      },
      cellRenderer: toolTipRenderer,
    },
    {
      headerName: 'Action', minWidth: 180, flex: 2,
      valueGetter: params => getCourseHistoryActionDisplay(params.data.history_action),
      cellRenderer: toolTipRenderer,
    },
    {
      headerName: 'Details', minWidth: 150, flex: 4,
      valueGetter: params => {
        const pageTitle = params.data.course_page_title;
        const detail = params.data.detail_text;
        if (pageTitle && pageTitle !== detail) {
          return detail ? `${pageTitle} - ${detail}` : pageTitle;
        }
        return detail;
      },
      cellRenderer: toolTipRenderer,
    },
    {
      headerName: 'Data', minWidth: 150, flex: 4,
      valueGetter: params => {
        const data = params.data.data;
        return data && Object.keys(data).length > 0 ? JSON.stringify(data) : '';
      },
      cellRenderer: toolTipRenderer,
    },
    ...(showAllConf ? [{field: 'conf', headerName: 'Conf', minWidth: 120}] : []),
  ];

  const defaultColDefs = {
    sortable: true,
    filter: 'agTextColumnFilter',
    resizable: true,
  };

  function quickFilterChanged(newValue) {
    setQuickFilter(newValue);
    if (gridRef?.current?.api) {
      gridRef?.current?.api.setGridOption('quickFilterText', newValue);
    } else {
      console.warn('cannot set quick filter as api is not available');
    }
  }

  return (
    <Fragment>

      {filterUserId &&
        <div className="mb-3">
          Showing activity for user_id={filterUserId} {filterUserData?.email}
        </div>
      }

      <div className='d-flex flex-wrap row-gap-2 mb-3'>
        <div className='ms-1 me-1'>
          <Form.Check
            type="checkbox"
            label="Show All Conf"
            id="course_history_show_all_conf"
            checked={showAllConf}
            onChange={e => setShowAllConf(e.target.checked)}
            style={{maxWidth: '120px'}}
          />
        </div>

        <div className="d-flex ms-1 me-1">
          <Form.Group controlId="course_history_date_filter_start" className="flex-grow-1 me-1">
            <FloatingLabel controlId="course_history_date_filter_start" label="Start">
              <Form.Control type="date"
                            placeholder="Start"
                            name="date_filter_start"
                            value={dateFilterStart}
                            onChange={e => setDateFilterStart(e.target.value)}/>
            </FloatingLabel>
          </Form.Group>
          <Form.Group controlId="course_history_date_filter_end" className="flex-grow-1 ms-1">
            <FloatingLabel controlId="course_history_date_filter_end" label="End">
              <Form.Control type="date"
                            placeholder="End"
                            name="date_filter_end"
                            value={dateFilterEnd}
                            onChange={e => setDateFilterEnd(e.target.value)}/>
            </FloatingLabel>
          </Form.Group>
        </div>

        <div className='ms-1 me-1'>
          <Form.Group controlId="course_history_course_id">
            <Form.Control
              className="form-select"
              as="select"
              value={courseId || ''}
              onChange={e => setCourseId(e.target.value)}
            >
              <option value='' key=''>Filter by Course...</option>
              {courseList?.map(course => {
                return <option value={course.id} key={course.id}>{course.title}</option>
              })}
            </Form.Control>
          </Form.Group>
        </div>

        <div className='ms-1 me-1'>
          <Form.Group controlId="course_history_action">
            <Form.Control
              className="form-select"
              as="select"
              value={action || ''}
              onChange={e => setAction(e.target.value)}
            >
              <option value='' key=''>Filter by Action...</option>
              {actionList?.map(action => {
                return <option value={action} key={action}>{getCourseHistoryActionDisplay(action)}</option>
              })}
            </Form.Control>
          </Form.Group>
        </div>

        <div className='ms-1 me-1'>
          <Form.Group controlId="course_history_user_search">
            <FloatingLabel controlId="course_history_user_search" label="User Name/Email">
              <Form.Control type="text"
                            placeholder="User Name/Email"
                            name="user_search"
                            style={{maxWidth: '180px'}}
                            value={userSearch}
                            onChange={e => setUserSearch(e.target.value)}/>
            </FloatingLabel>
          </Form.Group>
        </div>

        <div className='ms-1 me-1'>
          <Form.Group controlId="course_history_page_num">
            <FloatingLabel controlId="course_history_page_num" label="Pg #">
              <Form.Control type="number"
                            step="1"
                            min="1"
                            placeholder="Pg #"
                            name="page_num"
                            style={{maxWidth: '80px'}}
                            value={pageNum}
                            onChange={e => setPageNum(e.target.value)}/>
            </FloatingLabel>
          </Form.Group>
        </div>

        <div className='ms-1 me-1'>
          <Form.Group controlId="course_history_page_size">
            <FloatingLabel controlId="course_history_page_size" label="Page Size">
              <Form.Control type="number"
                            step="1000"
                            min="1"
                            placeholder="Page Size"
                            name="page_size"
                            style={{maxWidth: '100px'}}
                            value={pageSize}
                            onChange={e => setPageSize(e.target.value)}/>
            </FloatingLabel>
          </Form.Group>
        </div>

        <div className='ms-1 me-1'>
          <Button onClick={() => loadHistory()}>Apply Filters </Button>
        </div>
      </div>

      {errors && errors.length > 0 &&
        <div className="text-center mb-3 usacm-error-message">
          {getErrorMessageForAllErrors(errors)}
        </div>
      }

      <div className='flex-grow-1 ms-1 mb-2'>
        <Form.Control type="text"
                      id="course-history-quick-filter"
                      placeholder='Search Results...'
                      name="quick-filter"
                      value={quickFilter}
                      onChange={e => quickFilterChanged(e.target.value)}
                      style={{minWidth: '150px'}}/>
      </div>
      <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
        <AgGridReact
          ref={gridRef}
          rowData={historyList}
          columnDefs={colDefs}
          defaultColDef={defaultColDefs}
          domLayout={hasFewRows ? "autoHeight" : "normal"}
          gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
          getRowId={(params) => String(params.data.id)} // prevent re-rendering the whole table when data changes
        />
      </div>
    </Fragment>
  );
}
